import React, { useState, useEffect, useCallback } from 'react';
import { X, Calendar, Package } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Select, SelectTrigger, SelectContent, SelectItem } from '../ui/Select';
import { apiService } from '../../services/api';
import { useToastNotifications } from '../ui/Toast';

interface ClientRenewModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onSuccess: () => void;
  /** Chamado após renovação com sucesso, antes de onSuccess/onClose. Permite exibir RenewalSuccessModal no pai. */
  onRenewalSuccess?: (data: { client: any; renewalData: any }) => void;
}

interface PackageData {
  id: number;
  name: string;
  display_name: string;
  is_trial: number;
  official_credits: number;
  official_duration: number;
  official_duration_in: string;
  max_connections?: number;
}

const ClientRenewModal: React.FC<ClientRenewModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
  onRenewalSuccess
}) => {
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const { showWarning, showError } = useToastNotifications();

  const loadPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      const response = await apiService.getPackages(1, 100);
      const list: PackageData[] = response.data || [];
      setPackages(list);

      const currentPkgId =
        user?.package_id ??
        user?.bouquet ??
        user?.package?.id ??
        user?.member?.package_id;
      if (currentPkgId != null) {
        const match = list.find(
          (pkg) => pkg.is_trial < 1 && String(pkg.id) === String(currentPkgId)
        );
        if (match) {
          setSelectedPackage(String(match.id));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
      setFormError('Não foi possível carregar os pacotes. Tente novamente.');
    } finally {
      setLoadingPackages(false);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && user) {
      loadPackages();
      setIsClosing(false);
      setFormError('');
    }
  }, [isOpen, user, loadPackages]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPackage('');
      setLoading(false);
      setIsClosing(false);
      setFormError('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 100);
  };

  const handleRenew = async () => {
    if (!selectedPackage) {
      setFormError('Selecione um pacote para renovar o cliente');
      showWarning('Atenção', 'Selecione um pacote para renovar o cliente');
      return;
    }

    setFormError('');
    setLoading(true);
    try {
      const response = await apiService.renewUser(user.id, { package_id: selectedPackage });
      const data = response?.data ?? response;

      const selectedPackageData = packages.find(
        (pkg: PackageData) => pkg.id.toString() === selectedPackage
      );
      if (!selectedPackageData) {
        setFormError('Pacote não encontrado');
        showWarning('Atenção', 'Pacote não encontrado');
        return;
      }

      const packageName = selectedPackageData.display_name || selectedPackageData.name;
      let formattedExpDate = '';
      if (data?.new_exp_date_formatted) {
        formattedExpDate = data.new_exp_date_formatted;
      } else if (data?.new_exp_date) {
        const expDate = new Date(data.new_exp_date);
        formattedExpDate = expDate.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        const duration = selectedPackageData.official_duration;
        const durationIn = selectedPackageData.official_duration_in;
        const newExpDate = new Date();
        if (durationIn === 'month') {
          newExpDate.setMonth(newExpDate.getMonth() + duration);
        } else if (durationIn === 'day') {
          newExpDate.setDate(newExpDate.getDate() + duration);
        } else if (durationIn === 'year') {
          newExpDate.setFullYear(newExpDate.getFullYear() + duration);
        }
        formattedExpDate = newExpDate.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      const newExpUnix = data?.new_exp_date
        ? Math.floor(new Date(data.new_exp_date).getTime() / 1000)
        : Math.floor(Date.now() / 1000);
      const renewalData = {
        new_exp_date: newExpUnix,
        new_exp_date_formatted: formattedExpDate,
        cost_paid: selectedPackageData.official_credits ?? 0
      };
      const passwordFromApi =
        data?.password ||
        data?.line?.password ||
        user.password ||
        '';
      const maxFromApi =
        data?.max_connections ??
        data?.line?.max_connections ??
        selectedPackageData.max_connections ??
        user.max_connections ??
        1;
      const clientForModal = {
        id: String(user.id),
        username: user.username ?? '',
        password: passwordFromApi,
        email: user.member?.email ?? user.email ?? '',
        whatsapp: user.whatsapp ?? user.contact ?? '',
        contact: user.whatsapp ?? user.contact ?? '',
        max_connections: maxFromApi,
        maxConnections: maxFromApi,
        is_trial: user.is_trial ?? 0,
        system: user.system ?? 1,
        package_name: packageName,
        packageName
      };

      if (onRenewalSuccess) {
        onRenewalSuccess({ client: clientForModal, renewalData });
        onClose();
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Erro ao renovar usuário:', error);
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        error?.message ||
        'Erro ao renovar usuário';
      // Mantém o modal aberto; toast não bloqueia toque no smartphone
      setFormError(errorMessage);
      showError('Erro na Renovação', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || isClosing) return null;

  function filterPackages() {
    return packages
      .filter((pkg) => pkg.is_trial < 1)
      .slice()
      .sort((a, b) => {
        const na = String(a.display_name || a.name || '').toLocaleLowerCase('pt-BR');
        const nb = String(b.display_name || b.name || '').toLocaleLowerCase('pt-BR');
        return na.localeCompare(nb, 'pt-BR', { sensitivity: 'base' });
      });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4 overflow-y-auto">
      <div
        className="bg-white rounded-t-2xl sm:rounded-lg p-5 sm:p-6 w-full max-w-md sm:mx-4 max-h-[92vh] overflow-y-auto shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="renew-client-title"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="renew-client-title" className="text-xl font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 shrink-0" />
            Renovar Cliente
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 p-2 -mr-2"
            disabled={loading}
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label>Cliente</Label>
            <Input value={user?.username || ''} disabled className="bg-gray-50" />
          </div>

          <div>
            <Label>Pacote</Label>
            {loadingPackages ? (
              <div className="text-sm text-gray-500 py-2">Carregando pacotes...</div>
            ) : (
              <Select
                value={selectedPackage}
                onValueChange={(v) => {
                  setSelectedPackage(v);
                  setFormError('');
                }}
                placeholder="Selecione um pacote"
              >
                <SelectTrigger>Selecione um pacote</SelectTrigger>
                <SelectContent>
                  {filterPackages().map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id.toString()}>
                      {pkg.display_name || pkg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {formError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 break-words">
              {formError}
            </div>
          ) : null}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 pb-[env(safe-area-inset-bottom)]">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleRenew}
              disabled={loading || !selectedPackage || loadingPackages}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Package className="w-4 h-4" />
              )}
              Renovar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientRenewModal;
